import type { ErrorGroup, TxSummary } from '@apm/contracts';
import { TxSearchResult } from '@apm/contracts';
import { useMemo, useState } from 'react';
import { toQuery } from '../api';
import { useQuery } from '../hooks/useQuery';
import { TX_TYPE_TONE } from '../live/format';
import { currentQuery, routeHref } from '../router';
import { statusLevel, statusText } from '../status';
import { TIP, TX_TYPE_LABEL } from '../terms';
import { formatCount, formatDuration, formatFull, formatRelative, formatStamp } from '../time';
import { Cell, Chip, type Column, EmptyState, ErrorText, Loading, Row, Table } from '../ui';

/**
 * 같은 오류 묶음 표 (22-B회차, 빠진 정보 3).
 *
 * 한 줄이 원인 하나다(단계·예외 종류·상태 코드·DB 오류 코드·경로). 건수와 처음·마지막 시각으로
 * "새로 생긴 것인가, 계속 나던 것인가"를 가른다. 펼치면 그 묶음의 개별 요청이 아래 나온다 —
 * 조회는 검색 API에 경로·단계를 걸고, 나머지 키는 화면에서 맞춘다(검색에 예외 종류 조건이 없다).
 */

const COLUMNS: readonly Column[] = [
  { label: '심각도', width: 'w-24' },
  { label: '원인' },
  { label: '유형', width: 'w-28' },
  { label: '요청 경로', title: TIP.entry },
  { label: '건수', width: 'w-20', align: 'right' },
  { label: '처음', width: 'w-36' },
  { label: '마지막', width: 'w-36' },
  { label: '', width: 'w-24' },
];

const ROW_COLUMNS: readonly Column[] = [
  { label: '시각', width: 'w-40' },
  { label: '응답시간', width: 'w-28' },
  { label: '상태', width: 'w-32' },
  { label: '메시지' },
  { label: '인스턴스', width: 'w-44', title: TIP.instance },
];

/** 펼쳤을 때 한 묶음에서 보이는 최대 건수 */
const ROWS_PER_GROUP = 50;

/** 묶음의 키. 서버가 묶은 기준과 같다 */
function groupKey(group: ErrorGroup): string {
  return [
    group.service ?? '',
    group.level,
    group.errorType ?? '',
    group.httpStatus ?? '',
    group.sqlErrorCode ?? '',
    group.txType,
    group.entry,
  ].join('\u0000');
}

/** 검색 결과 행이 이 묶음에 드는가. 서버 묶기와 같은 키를 화면에서 맞춘다 */
function belongsTo(row: TxSummary, group: ErrorGroup): boolean {
  return (
    (group.service === undefined || row.service === group.service) &&
    statusLevel(row) === group.level &&
    row.errorType === group.errorType &&
    row.httpStatus === group.httpStatus &&
    row.sqlErrorCode === group.sqlErrorCode &&
    row.txType === group.txType &&
    row.entry === group.entry
  );
}

/** 원인 한 줄. 예외 종류가 가장 구체적이고, 없으면 상태 코드·DB 오류 코드다 */
function causeOf(group: ErrorGroup): string {
  const parts: string[] = [];
  if (group.errorType !== undefined) parts.push(group.errorType);
  if (group.httpStatus !== undefined) parts.push(`상태 코드 ${group.httpStatus}`);
  if (group.sqlErrorCode !== undefined) parts.push(`DB 오류 ${group.sqlErrorCode}`);
  return parts.length === 0 ? '원인 정보 없음' : parts.join(' · ');
}

export function ErrorGroups({
  groups,
  range,
  instance,
  service,
  showService = false,
}: {
  groups: readonly ErrorGroup[];
  range: { from?: number; to?: number };
  instance?: string;
  service?: string;
  showService?: boolean;
}) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());

  const toggle = (key: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (groups.length === 0) return null;

  return (
    <Table
      columns={showService ? [{ label: '서비스', width: 'w-40' }, ...COLUMNS] : COLUMNS}
      caption="같은 오류 묶음"
      minWidth="min-w-[1100px]"
    >
      {groups.map((group) => {
        const key = groupKey(group);
        const shown = open.has(key);
        return [
          <Row
            key={key}
            tone={group.level === 'error' ? 'error' : undefined}
            onClick={() => toggle(key)}
          >
            {showService ? (
              <Cell title={group.service} className="max-w-[180px] truncate">
                {group.service ?? '-'}
              </Cell>
            ) : null}
            <Cell>
              <Chip tone={group.level === 'error' ? 'error' : 'fail'}>
                {group.level === 'error' ? '오류' : '실패'}
              </Chip>
            </Cell>
            <Cell className="max-w-[360px]">
              <p className="truncate font-mono text-xs text-ink" title={causeOf(group)}>
                {causeOf(group)}
              </p>
              {group.lastMessage !== undefined ? (
                <p className="truncate text-xs text-muted" title={group.lastMessage}>
                  {group.lastMessage}
                </p>
              ) : null}
              {/* 서버 오류에만 있다. 어느 줄에서 났는지가 원인을 가르는 첫 단서다 (25회차) */}
              {group.lastFrame !== undefined ? (
                <p
                  className="truncate font-mono text-[11px] text-muted"
                  title={`${group.lastFrame}\n\n${TIP.errorFrames}`}
                >
                  {group.lastFrame}
                </p>
              ) : null}
            </Cell>
            <Cell>
              <Chip tone={TX_TYPE_TONE[group.txType]}>{TX_TYPE_LABEL[group.txType]}</Chip>
            </Cell>
            <Cell mono title={group.entry} className="max-w-[300px] truncate">
              {group.entry}
            </Cell>
            <Cell align="right" className="tabular font-semibold">
              {formatCount(group.count)}
            </Cell>
            <Cell nowrap tone="muted" className="tabular" title={formatFull(group.firstAt)}>
              {formatStamp(group.firstAt)}
            </Cell>
            <Cell nowrap className="tabular" title={formatFull(group.lastAt)}>
              {formatRelative(group.lastAt)}
            </Cell>
            <Cell nowrap>
              {/* 첫 칸이 아니라 마지막 칸의 단추가 키보드 경로다. 원인 열이 길어 앞에 두면 표가 흔들린다 */}
              <button
                type="button"
                aria-expanded={shown}
                onClick={(event) => {
                  event.stopPropagation();
                  toggle(key);
                }}
                className="rounded-md px-2 py-0.5 text-link transition-colors duration-150 hover:bg-band"
              >
                {shown ? '접기' : '펼치기'}
              </button>
            </Cell>
          </Row>,
          shown ? (
            <tr key={`${key}-rows`} className="border-b border-hair bg-band/60">
              <td colSpan={COLUMNS.length + (showService ? 1 : 0)} className="px-4 py-3">
                <GroupRows
                  group={group}
                  range={range}
                  instance={instance}
                  service={group.service ?? service}
                />
              </td>
            </tr>
          ) : null,
        ];
      })}
    </Table>
  );
}

/** 묶음 하나의 개별 요청. 펼쳤을 때만 조회한다 */
function GroupRows({
  group,
  range,
  instance,
  service,
}: {
  group: ErrorGroup;
  range: { from?: number; to?: number };
  instance?: string;
  service?: string;
}) {
  const url = `/api/tx/search${toQuery({
    from: range.from,
    to: range.to,
    entryExact: group.entry,
    txType: group.txType,
    status: group.level,
    instance,
    service,
    limit: ROWS_PER_GROUP * 4,
  })}`;
  const { loading, data, error } = useQuery(url, TxSearchResult);
  const rows = useMemo(
    () => (data?.rows ?? []).filter((row) => belongsTo(row, group)).slice(0, ROWS_PER_GROUP),
    [data, group],
  );

  if (error !== undefined) return <ErrorText>{error}</ErrorText>;
  if (loading && data === undefined) return <Loading />;
  if (rows.length === 0) {
    // 서버에는 경로·종류·등급만 걸고 원인(예외·상태 코드·DB 오류 코드)은 받은 뒤 거른다. 같은 경로에
    // 다른 원인의 오류가 많이 쌓이면 받은 몫 안에 이 묶음이 없을 수 있다. 두 까닭을 다 적는다 (44회차)
    return (
      <EmptyState
        title="개별 요청을 찾지 못했다"
        hint={[
          '요약 파일이 아직 안 쓰였을 수 있다.',
          `같은 경로에 다른 원인의 오류가 많으면 최근 ${formatCount(ROWS_PER_GROUP * 4)}건 안에 이 묶음의 요청이 없을 수도 있다. 기간을 좁혀 볼 것`,
        ]}
      />
    );
  }
  return (
    <div className="space-y-2">
      <p className="tabular text-xs text-muted">
        최근 {formatCount(rows.length)}건
        {group.count > rows.length ? ` (묶음 ${formatCount(group.count)}건 중)` : ''}. 시각을 누르면
        상세로 간다
      </p>
      <Table
        columns={ROW_COLUMNS}
        caption={`${causeOf(group)} 개별 요청`}
        minWidth="min-w-[900px]"
        maxHeight="max-h-[360px]"
      >
        {rows.map((row) => (
          <Row key={row.txId}>
            <Cell nowrap className="tabular">
              <a
                href={routeHref({
                  name: 'detail',
                  traceId: row.traceId,
                  from: 'errors',
                  service: row.service,
                  back: currentQuery(),
                })}
                title={formatFull(row.startedAt)}
                className="-mx-1 block rounded px-1 text-link no-underline hover:underline"
              >
                {formatStamp(row.startedAt)}
              </a>
            </Cell>
            <Cell mono nowrap className="tabular">
              {formatDuration(row.durationMs)}
            </Cell>
            <Cell nowrap tone={statusLevel(row) === 'error' ? 'error' : 'fail'}>
              {statusText(row)}
            </Cell>
            <Cell className="max-w-[420px] truncate" title={row.errorMessage}>
              {row.errorMessage ?? <span className="text-muted">메시지 없음</span>}
            </Cell>
            <Cell mono tone="muted" title={row.instance} className="max-w-[200px] truncate">
              {row.instance ?? '-'}
            </Cell>
          </Row>
        ))}
      </Table>
    </div>
  );
}
